import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, FileCode, ArrowRight, Plus, Minus, X, AlertTriangle } from 'lucide-react';
import { parseEDI, formatEDISegments, formatValue, translateCode, getSegmentNameFromLibrary as getSegmentName, getElementNameFromLibrary as getElementName } from '../services/ediService';
import EDISummaryHeader from './EDISummaryHeader';

/**
 * EDI Pretty Viewer - Human-readable EDI display with content-based diff
 * Shows segment names, element descriptions, and code translations
 * Uses x12-parser library for accurate parsing + local dictionary for metadata
 * Uses LCS algorithm to properly align segments regardless of insertions
 */
const EDIPrettyViewer = ({
    source,
    target,
    sourceName = 'Source',
    targetName = 'Target',
    ediDefinitions = null,
    isViewMode = false,
    searchQuery = '',
    searchType = 'all',
    currentMatchIndex = 0,
    changesFilter = 'all',
    onSearchMatchesChange,
    viewerSettings = {},
    highlightedSegmentId = null,
    onEdiStats,
    suggestedRenames = [],
    elementChanges = [],
    typeChanges = [],
    showSchemaDriftOnly = false
}) => {
    const [expandedSegments, setExpandedSegments] = useState(new Set());
    const [expandedLoops, setExpandedLoops] = useState(new Set()); // For loop hierarchy tree
    const matchRefs = useRef([]);

    // Detect file-wide delimiter changes from typeChanges
    const delimiterChange = useMemo(() => {
        if (!typeChanges || !Array.isArray(typeChanges)) return null;
        return typeChanges.find(tc =>
            Array.isArray(tc.path) &&
            tc.path[0] === 'ISA' &&
            tc.path[1] === 'Element Delimiter'
        );
    }, [typeChanges]);

    // Toggle loop expansion
    const toggleLoop = (loopId) => {
        setExpandedLoops(prev => {
            const newSet = new Set(prev);
            if (newSet.has(loopId)) {
                newSet.delete(loopId);
            } else {
                newSet.add(loopId);
            }
            return newSet;
        });
    };


    // Parse segments from source and target
    const sourceSegments = useMemo(() => parseEdiSegments(source), [source]);
    const targetSegments = useMemo(() => parseEdiSegments(target), [target]);

    // Auto-expand loops on initial load
    useEffect(() => {
        if (viewerSettings.showLoopHierarchy) {
            const allLoopIds = new Set();
            sourceSegments.forEach((seg, idx) => {
                const loopCtx = getLoopContext(seg.segment_id);
                if (loopCtx) {
                    allLoopIds.add(`${loopCtx.loop}-${idx}`);
                }
            });
            setExpandedLoops(allLoopIds);
        }
    }, [sourceSegments, viewerSettings.showLoopHierarchy]);

    // Compute aligned diff using content-based matching
    const alignedDiff = useMemo(() => {
        const diff = computeAlignedDiff(sourceSegments, targetSegments, suggestedRenames);

        // Inject element-level changes into the aligned diff
        if (elementChanges && Array.isArray(elementChanges) && elementChanges.length > 0) {
            console.log('[EDI Diff] Injecting element changes:', elementChanges.length, elementChanges);
            console.log('[EDI Diff] Diff entries:', diff.length, diff.map(d => ({
                srcId: d.source?.segment_id, srcNum: d.source?.segment_num,
                tgtId: d.target?.segment_id, tgtNum: d.target?.segment_num,
                status: d.status
            })));

            elementChanges.forEach(change => {
                const uiPosition = change.element_idx + 1; // Backend is 0-indexed

                // Find matching segment in diff
                const matchedDiff = diff.find(d =>
                    (d.source && d.source.segment_id === change.segment_id && d.source.segment_num === change.segment_num) ||
                    (!d.source && d.target && d.target.segment_id === change.segment_id && d.target.segment_num === change.segment_num)
                );

                console.log(`[EDI Diff] Change: ${change.segment_id}@${change.segment_num} el${change.element_idx} (${change.change_type}) → matchedDiff:`, matchedDiff ? 'FOUND' : 'NOT FOUND');

                if (matchedDiff) {
                    if (matchedDiff.source) {
                        let el = matchedDiff.source.elements.find(e => e.position === uiPosition);
                        // Fix: parentheses needed for operator precedence
                        if (!el && (change.change_type === 'added')) {
                            el = { position: uiPosition, name: `Element ${uiPosition}`, value: '', display: '', change: null };
                            matchedDiff.source.elements.push(el);
                        }
                        if (el) {
                            el.change = change;
                            console.log(`[EDI Diff]   → Set source el ${uiPosition} .change =`, change.change_type);
                        }
                    }
                    if (matchedDiff.target) {
                        const el = matchedDiff.target.elements.find(e => e.position === uiPosition);
                        if (el) {
                            el.change = change;
                            console.log(`[EDI Diff]   → Set target el ${uiPosition} .change =`, change.change_type);
                        }
                    }
                }
            });
        }

        return diff;
    }, [sourceSegments, targetSegments, suggestedRenames, elementChanges]);

    // Filter by changes if in compare mode
    const [allExpanded, setAllExpanded] = useState(false);
    const toggleAll = () => {
        if (allExpanded) {
            setExpandedSegments(new Set());
            setExpandedLoops(new Set());
            setAllExpanded(false);
        } else {
            setExpandedSegments(new Set(filteredDiff.map((_, i) => i)));

            const allLoopIds = new Set();
            sourceSegments.forEach((seg, idx) => {
                const loopCtx = getLoopContext(seg.segment_id);
                if (loopCtx) {
                    allLoopIds.add(`${loopCtx.loop}-${idx}`);
                }
            });
            setExpandedLoops(allLoopIds);
            setAllExpanded(true);
        }
    };

    const filteredDiff = useMemo(() => {
        let result = alignedDiff;

        if (isViewMode) return result;

        // When structural toggle is on, tag each segment as structural or not
        if (showSchemaDriftOnly) {
            result = result.map(d => {
                // Added/removed/renamed segments are always structural
                if (d.status === 'added' || d.status === 'removed' || d.status === 'renamed') {
                    return { ...d, isStructuralHighlight: true };
                }

                // If it's just 'changed', only count as structural if elements changed
                if (d.status === 'changed' && elementChanges && Array.isArray(elementChanges)) {
                    const key = `${d.source?.segment_id}_${d.source?.segment_num}`;
                    const hasElemChange = elementChanges.some(c => c.segment_id === d.source?.segment_id && c.segment_num === d.source?.segment_num);
                    if (hasElemChange) {
                        return { ...d, isStructuralHighlight: true };
                    }
                }
                return { ...d, isStructuralHighlight: false };
            });
        }

        if (changesFilter !== 'all') {
            result = result.filter(d => {
                if (changesFilter === 'added') return d.status === 'added';
                if (changesFilter === 'removed') return d.status === 'removed';
                if (changesFilter === 'changed') return d.status === 'changed';
                return true;
            });
        }
        return result;
    }, [alignedDiff, changesFilter, showSchemaDriftOnly, isViewMode, elementChanges]);

    // Search matching logic - searches raw text, elements, segment ID, and names
    const searchMatches = useMemo(() => {
        if (!searchQuery.trim()) return [];
        const query = searchQuery.toLowerCase();
        const matches = [];

        filteredDiff.forEach((diff, idx) => {
            const segment = diff.source || diff.target;
            if (!segment) return;

            let isMatch = false;

            // Always search raw text (the actual EDI content like CUR*BY*USD)
            if (segment.raw?.toLowerCase().includes(query)) {
                isMatch = true;
            }

            // Search segment ID (ST, BEG, N1, etc.)
            if (searchType === 'all' || searchType === 'segment') {
                if (segment.segment_id?.toLowerCase().includes(query)) isMatch = true;
            }

            // Search element values
            if (searchType === 'all' || searchType === 'value' || searchType === 'code') {
                if (segment.elements?.some(el => el?.toString().toLowerCase().includes(query))) isMatch = true;
            }

            // Search field names/descriptions
            if (searchType === 'all' || searchType === 'field') {
                if (segment.segment_name?.toLowerCase().includes(query)) isMatch = true;
            }

            if (isMatch) matches.push(idx);
        });

        return matches;
    }, [filteredDiff, searchQuery, searchType]);

    // Report matches to parent (only when contents actually change)
    const prevMatchesRef = useRef([]);
    useEffect(() => {
        if (onSearchMatchesChange) {
            const prev = prevMatchesRef.current;
            const changed = prev.length !== searchMatches.length || prev.some((v, i) => v !== searchMatches[i]);
            if (changed) {
                prevMatchesRef.current = searchMatches;
                onSearchMatchesChange(searchMatches);
            }
        }
    }, [searchMatches, onSearchMatchesChange]);

    // Auto-scroll to current match
    useEffect(() => {
        if (currentMatchIndex > 0 && searchMatches.length > 0) {
            const matchIdx = searchMatches[currentMatchIndex - 1];
            if (matchRefs.current[matchIdx]) {
                matchRefs.current[matchIdx].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [currentMatchIndex, searchMatches]);

    // (elementChangesMap was removed here to inject direct to alignedDiff elements)

    // Toggle individual segment detail view (shows inline expand)
    const toggleSegment = (index) => {
        // Toggle inline expansion
        setExpandedSegments(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    // Get transaction type name
    const getTransactionName = (data) => {
        if (data?._transaction_type) return data._transaction_type;
        if (data?.detected_format) {
            const code = data.detected_format.replace('x12_', '');
            return `EDI ${code}`;
        }
        return 'EDI Document';
    };

    // Count changes
    const stats = useMemo(() => {
        const added = alignedDiff.filter(d => d.status === 'added').length;
        const removed = alignedDiff.filter(d => d.status === 'removed').length;
        const changed = alignedDiff.filter(d => d.status === 'changed').length;
        return { added, removed, changed };
    }, [alignedDiff]);

    // Report stats back to parent for the summary banner (only when values change)
    const prevStatsRef = useRef(null);
    useEffect(() => {
        if (onEdiStats) {
            const prev = prevStatsRef.current;
            if (!prev || prev.added !== stats.added || prev.removed !== stats.removed || prev.changed !== stats.changed) {
                prevStatsRef.current = stats;
                onEdiStats(stats);
            }
        }
    }, [stats, onEdiStats]);

    // Line item segment types that should be rendered as tables
    const LINE_ITEM_SEGMENTS = ['PO1', 'IT1', 'SN1', 'ACK', 'POC'];

    // Group consecutive line item segments for tabular display
    const groupedDiff = useMemo(() => {
        const groups = [];
        let currentTableItems = [];
        let tableStartIndex = -1;
        let currentLoopItems = [];
        let currentLoopId = null;
        let loopStartIndex = -1;

        const flushTable = () => {
            if (currentTableItems.length > 0) {
                groups.push({
                    type: 'lineItemTable',
                    items: currentTableItems,
                    segmentType: currentTableItems[0].diff.source?.segment_id || currentTableItems[0].diff.target?.segment_id,
                    startIndex: tableStartIndex
                });
                currentTableItems = [];
            }
        };

        const flushLoop = () => {
            if (currentLoopItems.length > 0) {
                groups.push({
                    type: 'loop',
                    items: currentLoopItems,
                    loopId: currentLoopId,
                    loopName: LOOP_INFO[currentLoopId]?.name || 'Loop',
                    startIndex: loopStartIndex
                });
                currentLoopItems = [];
                currentLoopId = null;
            }
        };

        filteredDiff.forEach((diff, idx) => {
            const segment = diff.source || diff.target;
            const segmentId = segment?.segment_id;

            // 1. Table Grouping
            const isTabular = viewerSettings.tabularLineItems && LINE_ITEM_SEGMENTS.includes(segmentId);

            // 2. Loop Grouping
            const loopCtx = (viewerSettings.groupLoopSegments || viewerSettings.showLoopHierarchy) ? getLoopContext(segmentId) : null;
            const isLoopTrigger = loopCtx && (loopCtx.parent === 'ST' || loopCtx.parent === 'GS');
            const isLoopChild = loopCtx && !isLoopTrigger;

            if (isTabular) {
                flushLoop();
                if (currentTableItems.length === 0) tableStartIndex = idx;
                currentTableItems.push({ diff, originalIndex: idx });
            } else if (isLoopTrigger) {
                flushTable();
                flushLoop();
                currentLoopId = segmentId;
                loopStartIndex = idx;
                currentLoopItems.push({ diff, originalIndex: idx });
            } else if (isLoopChild && currentLoopId && loopCtx.loop === LOOP_INFO[currentLoopId]?.loop) {
                currentLoopItems.push({ diff, originalIndex: idx });
            } else {
                flushTable();
                flushLoop();
                groups.push({ type: 'segment', diff, originalIndex: idx });
            }
        });

        flushTable();
        flushLoop();

        return groups;
    }, [filteredDiff, viewerSettings.tabularLineItems, viewerSettings.groupLoopSegments, viewerSettings.showLoopHierarchy]);

    // Check if a segment index is a search match
    const isSearchMatch = (idx) => searchMatches.includes(idx);
    const isCurrentMatch = (idx) => searchMatches[currentMatchIndex - 1] === idx;

    return (
        <div className="edi-pretty-viewer flex flex-col h-full bg-[#1e1e1e] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-[#252526] border-b border-[#3c3c3c]">
                <div className="flex items-center gap-4">
                    <FileCode size={18} className="text-blue-400" />
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-300">
                            {getTransactionName(source)}
                        </span>
                        {source?.detected_format !== target?.detected_format && target?.detected_format && (
                            <>
                                <ArrowRight size={14} className="text-gray-400" />
                                <span className="text-sm font-medium text-gray-300">
                                    {getTransactionName(target)}
                                </span>
                            </>
                        )}
                        <button
                            onClick={toggleAll}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-blue-900/30 text-blue-300 hover:bg-blue-900/50 transition-colors"
                            title={allExpanded ? "Collapse all" : "Expand all details and loops"}
                        >
                            {allExpanded ? (
                                <><ChevronUp size={12} /> Collapse All</>
                            ) : (
                                <><ChevronDown size={12} /> Expand All</>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* EDI File Overview / Summary */}
            <EDISummaryHeader source={source} target={target} isViewMode={isViewMode} />

            {/* Aligned Segment List */}
            <div className="flex-1 overflow-auto relative">
                {delimiterChange && (
                    <div className="mx-3 mt-3 mb-3 bg-amber-900/20 border border-amber-700/50 rounded-lg p-3 flex gap-3 shadow-sm">
                        <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={18} />
                        <div>
                            <h4 className="text-sm font-bold text-amber-300">EDI Delimiter Swap Detected</h4>
                            <p className="text-amber-400/90 mt-1 leading-relaxed">
                                The partner changed the file's element separator from <code className="bg-amber-900/50 px-1 py-0.5 rounded font-bold">{delimiterChange.old_type}</code> to <code className="bg-amber-900/50 px-1 py-0.5 rounded font-bold">{delimiterChange.new_type}</code>.
                                While the payload data remains identical, this formatting change will critically break downstream automated parsers that expect the original delimiter.
                            </p>
                        </div>
                    </div>
                )}

                {/* Column Headers */}
                <div className="flex w-fit min-w-full">
                    <div className="flex-1 min-w-0 border-r border-[#3c3c3c]">
                        <div className="px-2 py-1.5 bg-[#252526] border-b border-[#3c3c3c]">
                            <span className="text-xs font-medium text-blue-400">{sourceName}</span>
                        </div>
                    </div>
                    {!isViewMode && (
                        <div className="flex-1 min-w-0">
                            <div className="px-2 py-1.5 bg-[#252526] border-b border-[#3c3c3c]">
                                <span className="text-xs font-medium text-green-400">{targetName}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Paired Rows - source and target in same flex row for alignment */}
                <div>
                    {groupedDiff.map((group, groupIdx) => {
                        if (group.type === 'lineItemTable') {
                            return (
                                <div key={`table-${groupIdx}`} className="flex w-fit min-w-full">
                                    <div className={`flex-1 min-w-0 ${!isViewMode ? 'border-r border-[#3c3c3c]' : ''}`}>
                                        <LineItemTable
                                            items={group.items}
                                            side="source"
                                            isSearchMatch={isSearchMatch}
                                            isCurrentMatch={isCurrentMatch}
                                            matchRefs={matchRefs}
                                            isViewMode={isViewMode}
                                            highlightedSegmentId={highlightedSegmentId}
                                        />
                                    </div>
                                    {!isViewMode && (
                                        <div className="flex-1 min-w-0">
                                            <LineItemTable
                                                items={group.items}
                                                side="target"
                                                isSearchMatch={isSearchMatch}
                                                isCurrentMatch={isCurrentMatch}
                                                highlightedSegmentId={highlightedSegmentId}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        if (group.type === 'loop') {
                            const loopId = `${group.loopId}-${group.startIndex}`;
                            const isExpanded = expandedLoops.has(loopId);
                            const trigger = group.items[0];

                            return (
                                <div key={`loop-${groupIdx}`} className="loop-group border-b border-[#3c3c3c]">
                                    {/* Trigger Row */}
                                    <div className="flex relative w-fit min-w-full">
                                        <div className={`flex-1 min-w-0 ${!isViewMode ? 'border-r border-[#3c3c3c]' : ''}`}>
                                            <SegmentRow
                                                index={trigger.originalIndex}
                                                segment={trigger.diff.source}
                                                isExpanded={expandedSegments.has(trigger.originalIndex)}
                                                onToggle={() => toggleSegment(trigger.originalIndex)}
                                                status={isViewMode ? 'unchanged' : ((showSchemaDriftOnly && !trigger.diff.isStructuralHighlight && trigger.diff.status === 'changed') ? 'unchanged' : trigger.diff.status)}
                                                side="source"
                                                isSearchMatch={isSearchMatch(trigger.originalIndex)}
                                                isCurrentMatch={isCurrentMatch(trigger.originalIndex)}
                                                ref={el => matchRefs.current[trigger.originalIndex] = el}
                                                showElementLabels={viewerSettings.showElementLabels}
                                                showLoopHierarchy={viewerSettings.showLoopHierarchy}
                                                showEmptyElements={viewerSettings.showEmptyElements}
                                                isHighlighted={highlightedSegmentId === `edi-segment-${trigger.diff.source?.segment_id}-${trigger.diff.source?.segment_num}`}
                                                id={`edi-segment-${trigger.diff.source?.segment_id}-${trigger.diff.source?.segment_num}`}
                                            />
                                        </div>
                                        {!isViewMode && (
                                            <div className="flex-1 min-w-0">
                                                <SegmentRow
                                                    index={trigger.originalIndex}
                                                    segment={trigger.diff.target}
                                                    isExpanded={expandedSegments.has(trigger.originalIndex)}
                                                    onToggle={() => toggleSegment(trigger.originalIndex)}
                                                    status={isViewMode ? 'unchanged' : ((showSchemaDriftOnly && !trigger.diff.isStructuralHighlight && trigger.diff.status === 'changed') ? 'unchanged' : trigger.diff.status)}
                                                    side="target"
                                                    isSearchMatch={isSearchMatch(trigger.originalIndex)}
                                                    isCurrentMatch={isCurrentMatch(trigger.originalIndex)}
                                                    isHighlighted={highlightedSegmentId === `edi-segment-${trigger.diff.target?.segment_id}-${trigger.diff.target?.segment_num}`}
                                                    showElementLabels={viewerSettings.showElementLabels}
                                                    showLoopHierarchy={viewerSettings.showLoopHierarchy}
                                                    showEmptyElements={viewerSettings.showEmptyElements}
                                                    id={`edi-segment-${trigger.diff.target?.segment_id}-${trigger.diff.target?.segment_num}`}
                                                />
                                            </div>
                                        )}
                                        {/* Loop Expand/Collapse Toggle */}
                                        <div className="absolute left-1 bottom-1.5 z-20 flex items-center justify-center">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleLoop(loopId); }}
                                                className="p-0.5 text-blue-500 hover:text-blue-400 transition-colors"
                                                title={isExpanded ? "Collapse Loop" : "Expand Loop"}
                                            >
                                                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Child Rows */}
                                    {isExpanded && group.items.slice(1).map((item, childIdx) => (
                                        <div key={`child-${childIdx}`} className="flex w-fit min-w-full bg-indigo-900/5">
                                            <div className={`flex-1 min-w-0 pl-4 ${!isViewMode ? 'border-r border-[#3c3c3c]' : ''}`}>
                                                <SegmentRow
                                                    index={item.originalIndex}
                                                    segment={item.diff.source}
                                                    isExpanded={expandedSegments.has(item.originalIndex)}
                                                    onToggle={() => toggleSegment(item.originalIndex)}
                                                    status={isViewMode ? 'unchanged' : ((showSchemaDriftOnly && !item.diff.isStructuralHighlight && item.diff.status === 'changed') ? 'unchanged' : item.diff.status)}
                                                    side="source"
                                                    isSearchMatch={isSearchMatch(item.originalIndex)}
                                                    isCurrentMatch={isCurrentMatch(item.originalIndex)}
                                                    ref={el => matchRefs.current[item.originalIndex] = el}
                                                    showElementLabels={viewerSettings.showElementLabels}
                                                    showLoopHierarchy={viewerSettings.showLoopHierarchy}
                                                    showEmptyElements={viewerSettings.showEmptyElements}
                                                    isHighlighted={highlightedSegmentId === `edi-segment-${item.diff.source?.segment_id}-${item.diff.source?.segment_num}`}
                                                    id={`edi-segment-${item.diff.source?.segment_id}-${item.diff.source?.segment_num}`}
                                                />
                                            </div>
                                            {!isViewMode && (
                                                <div className="flex-1 min-w-0 pl-4">
                                                    <SegmentRow
                                                        index={item.originalIndex}
                                                        segment={item.diff.target}
                                                        isExpanded={expandedSegments.has(item.originalIndex)}
                                                        onToggle={() => toggleSegment(item.originalIndex)}
                                                        status={isViewMode ? 'unchanged' : ((showSchemaDriftOnly && !item.diff.isStructuralHighlight && item.diff.status === 'changed') ? 'unchanged' : item.diff.status)}
                                                        side="target"
                                                        isSearchMatch={isSearchMatch(item.originalIndex)}
                                                        isCurrentMatch={isCurrentMatch(item.originalIndex)}
                                                        isHighlighted={highlightedSegmentId === `edi-segment-${item.diff.target?.segment_id}-${item.diff.target?.segment_num}`}
                                                        showElementLabels={viewerSettings.showElementLabels}
                                                        showLoopHierarchy={viewerSettings.showLoopHierarchy}
                                                        showEmptyElements={viewerSettings.showEmptyElements}
                                                        id={`edi-segment-${item.diff.target?.segment_id}-${item.diff.target?.segment_num}`}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            );
                        }

                        const { diff, originalIndex } = group;
                        const isChangedRow = diff.status === 'changed' || diff.status === 'added' || diff.status === 'removed';
                        const effectiveStatus = (showSchemaDriftOnly && !diff.isStructuralHighlight && diff.status === 'changed') ? 'unchanged' : diff.status;
                        return (
                            <div key={`row-${originalIndex}`} className={`flex w-fit min-w-full ${isChangedRow ? '-mt-px' : ''}`}>
                                <div className={`flex-1 min-w-0 ${!isViewMode ? 'border-r border-[#3c3c3c]' : ''}`}>
                                    <SegmentRow
                                        index={originalIndex}
                                        segment={diff.source}
                                        isExpanded={expandedSegments.has(originalIndex)}
                                        onToggle={() => toggleSegment(originalIndex)}
                                        status={isViewMode ? 'unchanged' : effectiveStatus}
                                        side="source"
                                        isSearchMatch={isSearchMatch(originalIndex)}
                                        isCurrentMatch={isCurrentMatch(originalIndex)}
                                        ref={el => matchRefs.current[originalIndex] = el}
                                        showElementLabels={viewerSettings.showElementLabels}
                                        showLoopHierarchy={viewerSettings.showLoopHierarchy}
                                        showEmptyElements={viewerSettings.showEmptyElements}
                                        isHighlighted={highlightedSegmentId === `edi-segment-${diff.source?.segment_id}-${diff.source?.segment_num}`}
                                        id={`edi-segment-${diff.source?.segment_id}-${diff.source?.segment_num}`}
                                    />
                                </div>
                                {!isViewMode && (
                                    <div className="flex-1 min-w-0">
                                        <SegmentRow
                                            index={originalIndex}
                                            segment={diff.target}
                                            isExpanded={expandedSegments.has(originalIndex)}
                                            onToggle={() => toggleSegment(originalIndex)}
                                            status={isViewMode ? 'unchanged' : effectiveStatus}
                                            side="target"
                                            isSearchMatch={isSearchMatch(originalIndex)}
                                            isCurrentMatch={isCurrentMatch(originalIndex)}
                                            isHighlighted={highlightedSegmentId === `edi-segment-${diff.target?.segment_id}-${diff.target?.segment_num}`}
                                            showElementLabels={viewerSettings.showElementLabels}
                                            showLoopHierarchy={viewerSettings.showLoopHierarchy}
                                            showEmptyElements={viewerSettings.showEmptyElements}
                                            id={`edi-segment-${diff.target?.segment_id}-${diff.target?.segment_num}`}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};


/**
 * Line Item Table - Renders consecutive PO1/IT1/SN1 segments as a table
 */
const LineItemTable = ({ items, side, isSearchMatch, isCurrentMatch, matchRefs, isViewMode = false, highlightedSegmentId }) => {
    if (!items || items.length === 0) return null;

    // Get segment type
    const segmentType = items[0].diff.source?.segment_id || items[0].diff.target?.segment_id;

    // Define columns based on segment type
    const getColumns = () => {
        switch (segmentType) {
            case 'PO1':
                return [
                    { key: 1, name: 'Line #', width: 'w-16' },
                    { key: 2, name: 'Qty', width: 'w-12' },
                    { key: 3, name: 'Unit', width: 'w-12' },
                    { key: 4, name: 'Price', width: 'w-16' },
                    { key: 6, name: 'ID Qual', width: 'w-14' },
                    { key: 7, name: 'Product ID', width: 'flex-1' },
                ];
            case 'IT1':
                return [
                    { key: 1, name: 'Line #', width: 'w-16' },
                    { key: 2, name: 'Qty', width: 'w-12' },
                    { key: 3, name: 'Unit', width: 'w-12' },
                    { key: 4, name: 'Price', width: 'w-16' },
                    { key: 6, name: 'ID Qual', width: 'w-14' },
                    { key: 7, name: 'Product ID', width: 'flex-1' },
                ];
            case 'SN1':
                return [
                    { key: 1, name: 'Qty Ship', width: 'w-16' },
                    { key: 2, name: 'Unit', width: 'w-12' },
                    { key: 3, name: 'Qty Ordered', width: 'w-20' },
                ];
            default:
                return [
                    { key: 1, name: 'Element 1', width: 'w-20' },
                    { key: 2, name: 'Element 2', width: 'w-20' },
                    { key: 3, name: 'Element 3', width: 'flex-1' },
                ];
        }
    };

    const columns = getColumns();

    // Get cell value from segment
    const getCellValue = (segment, colKey) => {
        if (!segment?.elements) return '';
        const element = segment.elements[colKey - 1];
        return element?.display || element?.value || '';
    };

    // Get row status styling — returns { className, style }.
    // Uses real borders for perfect grid rendering, collapsing via -mt-px on parents.
    const getRowStyle = (item) => {
        const { diff, originalIndex } = item;
        if (isCurrentMatch(originalIndex)) return { className: 'bg-amber-600/40', style: {} };
        if (isSearchMatch(originalIndex)) return { className: 'bg-amber-700/25', style: {} };

        if (!isViewMode) {
            if (diff.status === 'added' && side === 'target') return { className: 'bg-green-900/30 border border-green-700/70 relative z-10', style: {} };
            if (diff.status === 'removed' && side === 'source') return { className: 'bg-red-900/30 border border-red-700/70 relative z-10', style: {} };
            if (diff.status === 'changed') return { className: 'bg-yellow-900/20 border border-yellow-700/70 relative z-10', style: {} };
        }
        return { className: 'border-b border-gray-800', style: {} };
    };

    const segment = side === 'source' ? items[0].diff.source : items[0].diff.target;

    return (
        <div className="border-b border-[#3c3c3c]">
            {/* Table Header */}
            <div className="flex items-center gap-1 px-2 py-1.5 bg-indigo-900/20 border-b border-indigo-800">
                <span className="text-xs font-bold text-indigo-400">
                    {segmentType}
                </span>
                <span className="text-xs text-gray-400">
                    Line Items ({items.length})
                </span>
            </div>

            {/* Column Headers */}
            <div className="flex gap-1 px-2 py-1 bg-[#252526] border-b border-[#3c3c3c] text-xs font-medium text-gray-400">
                {columns.map(col => (
                    <span key={col.key} className={`${col.width} truncate`}>{col.name}</span>
                ))}
            </div>

            {/* Data Rows */}
            {items.map((item, idx) => {
                const { diff, originalIndex } = item;
                const rowSegment = side === 'source' ? diff.source : diff.target;

                // Handle placeholder for added/removed
                if (!rowSegment) {
                    return (
                        <div key={idx} className="flex gap-1 px-2 py-1 min-h-[28px] bg-gray-800/30 border-b border-gray-800">
                            {/* Empty placeholder row */}
                        </div>
                    );
                }

                // Check if this row is highlighted
                const isHighlighted = highlightedSegmentId === `edi-segment-${rowSegment.segment_id}-${rowSegment.segment_num}`;

                const { className: rowClass, style: rowStyle } = getRowStyle(item);

                return (
                    <div
                        key={idx}
                        id={`edi-segment-${rowSegment.segment_id}-${rowSegment.segment_num}`}
                        ref={matchRefs ? (el => matchRefs.current[originalIndex] = el) : undefined}
                        className={`flex gap-1 px-2 py-1 text-xs font-mono transition-all duration-500 hover:bg-gray-800/50 ${rowClass} ${isHighlighted ? 'ring-2 ring-inset ring-blue-400 bg-blue-900/30 z-20 relative shadow-lg' : ''}`}
                        style={rowStyle}
                    >
                        {columns.map(col => (
                            <span key={col.key} className={`${col.width} truncate text-gray-300`}>
                                {getCellValue(rowSegment, col.key)}
                            </span>
                        ))}
                        {/* Status indicator - only in Compare mode */}
                        {!isViewMode && diff.status === 'added' && side === 'target' && (
                            <Plus size={12} className="text-green-500 flex-shrink-0" />
                        )}
                        {!isViewMode && diff.status === 'removed' && side === 'source' && (
                            <Minus size={12} className="text-red-500 flex-shrink-0" />
                        )}
                        {!isViewMode && diff.status === 'changed' && (
                            <span className="text-yellow-500 font-bold text-xs flex-shrink-0 ml-1">~</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Individual segment row with status highlighting
 */
const SegmentRow = React.forwardRef(({ index, segment, isExpanded, onToggle, status, side, isSearchMatch, isCurrentMatch, isHighlighted, showElementLabels = false, showLoopHierarchy = false, showEmptyElements = true, id }, ref) => {
    // For removed segments, show placeholder on target side
    // For added segments, show placeholder on source side
    if (!segment) {
        const isPlaceholder = (status === 'removed' && side === 'target') ||
            (status === 'added' && side === 'source');
        if (isPlaceholder) {
            return (
                <div className="h-full px-1.5 py-1 min-h-[36px] bg-gray-900/20 border border-transparent border-b-gray-800">
                    {/* Empty placeholder for alignment */}
                </div>
            );
        }
        return null;
    }

    const getRowStyle = () => {
        // Search match highlighting
        if (isCurrentMatch) return { className: 'bg-amber-600/40 border-[0.5px] border-transparent border-b-gray-800', style: {} };
        if (isSearchMatch) return { className: 'bg-amber-700/25 border-[0.5px] border-transparent border-b-gray-800', style: {} };
        if (isHighlighted) return { className: 'ring-2 ring-blue-400 z-20 relative border-[0.5px] border-transparent border-b-gray-800', style: {} };

        // Status highlighting — real borders with relative z-index for mt-px collapsing
        if (status === 'added' && side === 'target') return { className: 'bg-green-900/30 border border-green-700/70 relative z-10', style: {} };
        if (status === 'removed' && side === 'source') return { className: 'bg-red-900/30 border border-red-700/70 relative z-10', style: {} };
        if (status === 'changed') return { className: 'bg-yellow-900/20 border border-yellow-700/70 relative z-10', style: {} };
        return { className: 'border border-transparent border-b-gray-800', style: {} };
    };

    const getStatusIcon = () => {
        if (status === 'added' && side === 'target') return <Plus size={12} className="text-green-500" />;
        if (status === 'removed' && side === 'source') return <Minus size={12} className="text-red-500" />;
        if (status === 'changed') return <span className="text-yellow-500 font-bold ml-0.5">~</span>;
        return null;
    };

    // Format element labels for collapsed view
    const getElementLabels = () => {
        if (!showElementLabels || !segment.elements || segment.elements.length === 0) return null;

        // Show first 4 elements with labels
        const displayElements = segment.elements.slice(0, 4).filter(el => el.value);
        if (displayElements.length === 0) return null;

        return (
            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                {displayElements.map((el, i) => (
                    <span key={i} className="text-xs">
                        <span className="text-gray-500">{el.name || `E${el.position}`}: </span>
                        <span className="text-gray-300 font-mono">{el.display || el.value}</span>
                    </span>
                ))}
                {segment.elements.length > 4 && (
                    <span className="text-xs text-gray-400">+{segment.elements.length - 4} more</span>
                )}
            </div>
        );
    };

    const { className: rowClass, style: rowInlineStyle } = getRowStyle();

    return (
        <div
            id={id}
            ref={ref}
            className={`flex flex-col h-full w-fit min-w-full px-1.5 py-1 cursor-pointer transition-all duration-500 hover:bg-gray-800/50 ${rowClass}`}
            style={rowInlineStyle}
            onClick={onToggle}
        >
            <div className="flex items-center gap-2">
                {/* Status icon */}
                <span className="w-4">
                    {getStatusIcon()}
                </span>

                {/* Line number */}
                <span className="text-xs text-gray-600 w-6 text-right font-mono">
                    {index + 1}
                </span>

                {/* Segment ID */}
                <span className="text-xs font-bold text-purple-400 w-8">
                    {segment.segment_id}
                </span>

                {/* Segment name */}
                <span className="text-xs text-gray-400 truncate flex-1">
                    {segment.segment_name || segment.segment_id}
                </span>

                {/* Loop hierarchy badge */}
                {showLoopHierarchy && getLoopContext(segment.segment_id) && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-900/30 text-blue-400">
                        Loop: {getLoopContext(segment.segment_id).loop}
                    </span>
                )}


            </div>

            {/* Element labels (when enabled) or raw content */}
            {!isExpanded && (
                <div className="ml-16">
                    {(() => {
                        const changedEls = status === 'changed' ? (segment.elements || []).filter(el => el.change) : [];
                        if (changedEls.length > 0) {
                            return (
                                <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                                    {changedEls.map((el, i) => (
                                        <span key={i} className="text-xs">
                                            <span className="text-gray-500">{el.name || `E${el.position}`}: </span>
                                            {side === 'source' && (
                                                <span className="text-red-400 font-mono font-bold">{el.change.old_value || '—'}</span>
                                            )}
                                            {side === 'target' && (
                                                <span className="text-green-400 font-mono font-bold">{el.change.new_value || '—'}</span>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            );
                        }
                        if (showElementLabels && segment.elements?.length > 0) {
                            return getElementLabels();
                        }
                        return (
                            <div className="text-xs font-mono text-gray-400 truncate">
                                {segment.raw?.substring(0, 60)}
                                {segment.raw?.length > 60 && '...'}
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* Inline expanded element details */}
            {isExpanded && segment.elements?.length > 0 && (
                <div className="ml-16 mt-1 mb-1">
                    <table className="w-full text-xs border-collapse">
                        <thead>
                            <tr className="border-b border-gray-700">
                                <th className="text-left py-1 px-2 text-gray-500 font-medium w-8">#</th>
                                <th className="text-left py-1 px-2 text-gray-500 font-medium">Element</th>
                                <th className="text-left py-1 px-2 text-gray-500 font-medium">Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {segment.elements
                                .filter(el => showEmptyElements || el.value || el.change)
                                .map((el, i) => {
                                    const isChanged = !!el.change;
                                    let valueColorClass = 'text-gray-200';

                                    if (isChanged) {
                                        if (side === 'source') valueColorClass = 'text-red-500 font-bold';
                                        if (side === 'target') valueColorClass = 'text-green-500 font-bold';
                                    }

                                    return (
                                        <tr key={i} className="border-b border-gray-800">
                                            <td className="py-1 px-2 text-gray-500 font-mono">{el.position}</td>
                                            <td className="py-1 px-2 text-gray-300 font-medium">{el.name || `Element ${el.position}`}</td>
                                            <td className={`py-1 px-2 font-mono ${valueColorClass}`}>{el.display || el.value || '—'}</td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
});

/**
 * Expanded segment detail panel showing element breakdown
 */
const SegmentDetailPanel = ({ expandedIndex, diff, isViewMode = false, onClose, elementChangesMap }) => {
    if (!diff) return null;

    const { source, target, status } = diff;
    const segment = source || target;
    if (!segment) return null;

    const elements = mergeElements(source?.elements || [], target?.elements || []);

    // Apply granular element change highlighting if available
    if (elementChangesMap && segment.segment_id && segment.segment_num) {
        elements.forEach(elem => {
            const key = `${segment.segment_id}-${segment.segment_num}-${elem.position}`;
            const change = elementChangesMap.get(key);
            if (change) {
                elem.changed = true;
                if (!isViewMode) {
                    elem.sourceDisplay = change.old_value;
                    elem.targetDisplay = change.new_value;
                }
            }
        });
    }

    return (
        <div className="border-b border-gray-700 bg-[#252526] p-4 shadow-lg">
            {/* Segment Header */}
            <div className="flex items-center gap-3 mb-3">
                <span className="text-lg font-bold text-purple-400">
                    {segment.segment_id}
                </span>
                <span className="text-sm font-medium text-gray-300">
                    {segment.segment_name}
                </span>
                {/* Only show status badges in Compare mode */}
                {!isViewMode && status === 'added' && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-900/30 text-green-400">
                        Added
                    </span>
                )}
                {!isViewMode && status === 'removed' && (
                    <span className="text-xs px-2 py-0.5 rounded bg-red-900/30 text-red-400">
                        Removed
                    </span>
                )}
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="ml-auto p-1 rounded hover:bg-gray-700 text-gray-300 transition-colors"
                    title="Close element details"
                >
                    <X size={16} />
                </button>
            </div>

            {/* Element Table */}
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-indigo-800">
                        <th className="text-left py-2 px-3 text-xs font-medium text-gray-400">Element</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-gray-400">
                            {isViewMode ? 'Value' : 'Source'}
                        </th>
                        {!isViewMode && (
                            <th className="text-left py-2 px-3 text-xs font-medium text-gray-400">Target</th>
                        )}
                        {!isViewMode && (
                            <th className="text-left py-2 px-3 text-xs font-medium text-gray-400 w-20">Status</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {elements.map((elem, idx) => (
                        <tr key={idx} className={`border-b border-indigo-900 ${!isViewMode && elem.changed ? 'bg-yellow-900/20' : ''}`}>
                            <td className="py-2 px-3 group relative">
                                <div
                                    className="font-medium text-gray-300 cursor-help"
                                    title={elem.description || `${elem.name} (Element ${elem.position})`}
                                >
                                    {elem.name || `Element ${elem.position}`}
                                </div>
                                {elem.description && (
                                    <div className="text-xs text-gray-400 truncate max-w-[200px]">{elem.description}</div>
                                )}
                                {/* Tooltip on hover */}
                                {elem.description && (
                                    <div className="absolute left-full top-0 ml-2 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 w-64 pointer-events-none">
                                        <div className="font-medium">{elem.name}</div>
                                        <div className="text-gray-300 mt-1">{elem.description}</div>
                                    </div>
                                )}
                            </td>
                            <td className="py-2 px-3 font-mono text-gray-400">
                                {elem.sourceDisplay || elem.sourceValue || '—'}
                            </td>
                            {!isViewMode && (
                                <td className="py-2 px-3 font-mono text-gray-400">
                                    {elem.targetDisplay || elem.targetValue || '—'}
                                </td>
                            )}
                            {!isViewMode && (
                                <td className="py-2 px-3">
                                    {elem.changed && (
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-800 text-yellow-200">
                                            Changed
                                        </span>
                                    )}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/**
 * LoopTree Component - Displays segments grouped into collapsible loops
 */
const LoopTree = ({
    hierarchy,
    expandedLoops,
    onToggleLoop,
    isViewMode,
    isSearchMatch,
    isCurrentMatch,
    matchRefs,
    highlightedSegmentId,
    showElementLabels,
    expandedSegments,
    toggleSegment
}) => {
    return (
        <div className="loop-tree">
            {hierarchy.map((item, idx) => {
                if (item.type === 'loop') {
                    const loopId = `${item.loopId}-${item.startIndex}`;
                    const isExpanded = expandedLoops.has(loopId);

                    return (
                        <div key={loopId} className="loop-group mb-1">
                            {/* Loop Header */}
                            <div
                                onClick={() => onToggleLoop(loopId)}
                                className="flex items-center gap-2 px-2 py-1.5 bg-indigo-900/20 border border-indigo-800 rounded cursor-pointer hover:bg-indigo-900/30 transition-colors"
                            >
                                {isExpanded ? (
                                    <ChevronDown size={14} className="text-indigo-400" />
                                ) : (
                                    <ChevronUp size={14} className="text-indigo-400" />
                                )}
                                <span className="text-xs font-semibold text-indigo-300">
                                    {item.loopName}
                                </span>
                                <span className="text-xs text-indigo-400">
                                    ({item.segments.length} segments)
                                </span>
                            </div>

                            {/* Loop Segments */}
                            {isExpanded && (
                                <div className="ml-4 mt-1 border-l-2 border-indigo-800 pl-2">
                                    {item.segments.map((segment) => (
                                        <SegmentRow
                                            key={`loop-${segment.index}`}
                                            index={segment.index}
                                            segment={segment}
                                            isExpanded={expandedSegments.has(segment.index)}
                                            onToggle={() => toggleSegment(segment.index)}
                                            status={isViewMode ? 'unchanged' : segment.status}
                                            side="source"
                                            isSearchMatch={isSearchMatch(segment.index)}
                                            isCurrentMatch={isCurrentMatch(segment.index)}
                                            ref={el => matchRefs.current[segment.index] = el}
                                            showElementLabels={showElementLabels}
                                            showLoopHierarchy={false}
                                            isHighlighted={highlightedSegmentId === `edi-segment-${segment.segment_id}-${segment.segment_num}`}
                                            id={`edi-segment-${segment.segment_id}-${segment.segment_num}`}
                                        />
                                    ))}
                                </div>
                            )
                            }
                        </div>
                    );
                } else {
                    // Standalone segment
                    const segment = item.segment;
                    return (
                        <SegmentRow
                            key={`seg-${segment.index}`}
                            index={segment.index}
                            segment={segment}
                            isExpanded={expandedSegments.has(segment.index)}
                            onToggle={() => toggleSegment(segment.index)}
                            status={isViewMode ? 'unchanged' : segment.status}
                            side="source"
                            isSearchMatch={isSearchMatch(segment.index)}
                            isCurrentMatch={isCurrentMatch(segment.index)}
                            ref={el => matchRefs.current[segment.index] = el}
                            showElementLabels={showElementLabels}
                            showLoopHierarchy={false}
                            isHighlighted={highlightedSegmentId === `edi-segment-${segment.segment_id}-${segment.segment_num}`}
                            id={`edi-segment-${segment.segment_id}-${segment.segment_num}`}
                        />
                    );
                }
            })}
        </div >
    );
};

// Helper: Parse EDI data into segment array
// Uses x12-parser library when raw EDI content is available,
// otherwise falls back to pre-parsed segment data from backend
function parseEdiSegments(data) {
    try {
        if (!data) return [];
        // Guard: if data is a string (e.g. backend failed to parse non-standard delimiters),
        // try parsing it as raw EDI content
        if (typeof data === 'string') {
            const parsed = parseEDI(data);
            if (parsed.success && parsed.segments?.length > 0) {
                return formatEDISegments(parsed.segments);
            }
            return [];
        }

        // If we have raw EDI content (from file upload), use x12-parser
        if (data._raw_content || (data.segments && typeof data.segments[0] === 'string')) {
            const rawContent = data._raw_content || data.segments?.join('\n');
            if (rawContent) {
                const parsed = parseEDI(rawContent);
                if (parsed.success) {
                    return formatEDISegments(parsed.segments);
                }
            }
        }

        // Check for segments
        const segments = data.segments;

        if (segments && Array.isArray(segments)) {
            return segments.map((seg, i) => ({
                segment_id: seg.segment_id,
                segment_num: seg.segment_num !== undefined ? seg.segment_num : (i + 1), // fallback for old data where this was missing
                segment_name: getSegmentName(seg.segment_id),
                raw: seg.raw || `${seg.segment_id}*${(seg.elements || []).join('*')}`,
                elements: (seg.elements || []).map((val, idx) => {
                    const position = idx + 1;
                    const formattedValue = formatValue(seg.segment_id, position, val);
                    const translatedValue = translateCode(seg.segment_id, position, val);
                    // Use formatted value if it's different from raw, otherwise use translated
                    const display = formattedValue !== val ? formattedValue : translatedValue;
                    return {
                        position: position,
                        value: val,
                        name: getElementName(seg.segment_id, position),
                        display: display,
                        change: null // explicit initialization
                    };
                })
            }));
        }

        return [];
    } catch (error) {
        console.error('Error parsing EDI segments:', error);
        return [];
    }
}

/**
 * Compute aligned diff using content-based matching (LCS-like algorithm)
 * Returns array of { source, target, status } where segments are aligned
 *
 * Matching priority:
 * 1. Exact raw match → unchanged
 * 2. Same segment_id at compatible position → changed (modified)
 * 3. Source not in target → removed
 * 4. Target not in source → added
 */
function computeAlignedDiff(sourceSegments, targetSegments, suggestedRenames = []) {
    // Build lookup sets for quick membership check
    const sourceRaws = new Set(sourceSegments.map(s => s.raw));
    const targetRaws = new Set(targetSegments.map(s => s.raw));

    // Map known segment swaps from the backend Drift Detection
    const knownSwaps = {};
    suggestedRenames.forEach(rename => {
        if (rename.type === 'edi_segment_change' && rename.segment_num) {
            knownSwaps[rename.segment_num] = rename.new_field;
        }
    });

    const result = [];
    let srcIdx = 0;
    let tgtIdx = 0;

    while (srcIdx < sourceSegments.length || tgtIdx < targetSegments.length) {
        const srcSeg = sourceSegments[srcIdx];
        const tgtSeg = targetSegments[tgtIdx];

        if (!srcSeg && tgtSeg) {
            // Remaining target segments are additions
            result.push({ source: null, target: tgtSeg, status: 'added' });
            tgtIdx++;
        } else if (srcSeg && !tgtSeg) {
            // Remaining source segments are removals
            result.push({ source: srcSeg, target: null, status: 'removed' });
            srcIdx++;
        } else if (srcSeg && tgtSeg && knownSwaps[srcSeg.segment_num] === tgtSeg.segment_id) {
            // Backend explicitly told us this is a segment swap (e.g. IEA -> IEF), pair them!
            result.push({ source: srcSeg, target: tgtSeg, status: 'changed' });
            srcIdx++;
            tgtIdx++;
        } else if (srcSeg.raw === tgtSeg.raw) {
            // Exact match - unchanged
            result.push({ source: srcSeg, target: tgtSeg, status: 'unchanged' });
            srcIdx++;
            tgtIdx++;
        } else if (srcSeg.segment_id === tgtSeg.segment_id) {
            // Same segment type but different content = modified (e.g., value rename)
            // This catches cases like HARDWAREPO → HARDWARE_ORDER within a BEG segment
            result.push({ source: srcSeg, target: tgtSeg, status: 'changed' });
            srcIdx++;
            tgtIdx++;
        } else if (!targetRaws.has(srcSeg.raw)) {
            // Source segment doesn't exist in target - removed
            result.push({ source: srcSeg, target: null, status: 'removed' });
            srcIdx++;
        } else if (!sourceRaws.has(tgtSeg.raw)) {
            // Target segment doesn't exist in source - added
            result.push({ source: null, target: tgtSeg, status: 'added' });
            tgtIdx++;
        } else {
            // Both exist but at different positions - show as modified pair
            // This handles cases where segment content changed
            result.push({ source: srcSeg, target: tgtSeg, status: 'changed' });
            srcIdx++;
            tgtIdx++;
        }
    }

    return result;
}

// Helper: Merge source and target elements for comparison
function mergeElements(sourceElements, targetElements) {
    const maxLen = Math.max(sourceElements.length, targetElements.length);
    const merged = [];

    for (let i = 0; i < maxLen; i++) {
        const src = sourceElements[i];
        const tgt = targetElements[i];

        merged.push({
            position: i + 1,
            name: src?.name || tgt?.name || `Element ${i + 1}`,
            description: src?.description || tgt?.description,
            sourceValue: src?.value,
            targetValue: tgt?.value,
            sourceDisplay: src?.display,
            targetDisplay: tgt?.display,
            changed: src?.value !== tgt?.value
        });
    }

    return merged;
}





// Loop definitions for hierarchy display
const LOOP_INFO = {
    'N1': { loop: 'N1', name: 'Party Loop', parent: 'ST' },
    'N2': { loop: 'N1', name: 'Party Loop', parent: 'N1' },
    'N3': { loop: 'N1', name: 'Party Loop', parent: 'N1' },
    'N4': { loop: 'N1', name: 'Party Loop', parent: 'N1' },
    'PER': { loop: 'N1', name: 'Party Loop', parent: 'N1' },
    'REF': { loop: 'N1', name: 'Party Loop', parent: 'N1' }, // REF can also be outside, but often in N1
    'PO1': { loop: 'PO1', name: 'Line Item Loop', parent: 'ST' },
    'PID': { loop: 'PO1', name: 'Line Item Loop', parent: 'PO1' },
    'MEA': { loop: 'PO1', name: 'Line Item Loop', parent: 'PO1' },
    'SCH': { loop: 'PO1', name: 'Line Item Loop', parent: 'PO1' },
    'DTM': { loop: 'PO1', name: 'Line Item Loop', parent: 'PO1' }, // DTM often inside PO1
    'SAC': { loop: 'SAC', name: 'Allowance/Charge', parent: 'PO1' }, // SAC can be its own loop
    'IT1': { loop: 'IT1', name: 'Invoice Line Loop', parent: 'ST' },
    'ITD': { loop: 'IT1', name: 'Invoice Line Loop', parent: 'IT1' },
    'HL': { loop: 'HL', name: 'Hierarchical Loop', parent: 'ST' },
    'LIN': { loop: 'HL', name: 'Hierarchical Loop', parent: 'HL' },
    'SN1': { loop: 'HL', name: 'Hierarchical Loop', parent: 'HL' },
    'PRF': { loop: 'HL', name: 'Hierarchical Loop', parent: 'HL' },
    'TD1': { loop: 'HL', name: 'Hierarchical Loop', parent: 'HL' },
    'TD5': { loop: 'HL', name: 'Hierarchical Loop', parent: 'HL' },
};

function getLoopContext(segmentId) {
    return LOOP_INFO[segmentId] || null;
}

/**
 * Build loop hierarchy from segments
 * Groups segments into collapsible loops based on LOOP_INFO definitions
 * Returns array of groups: { type: 'segment'|'loop', data: {...} }
 *
 * Logic:
 * - Loop "trigger" segments (like N1, PO1) always start a new loop group
 * - Loop "child" segments (like N2, N3, N4, PER, PID, SCH) continue current loop
 * - Non-loop segments break the loop and become standalone
 */
function buildLoopHierarchy(segments) {
    if (!segments || segments.length === 0) return [];

    const hierarchy = [];
    let currentLoop = null;
    let currentLoopSegments = [];

    // Define which segments are "triggers" that start a new loop instance
    // vs "child" segments that continue an existing loop
    const LOOP_TRIGGERS = new Set(['N1', 'PO1', 'IT1', 'HL', 'SAC']);

    segments.forEach((segment, index) => {
        const loopContext = getLoopContext(segment.segment_id);

        if (loopContext) {
            // This segment belongs to a loop
            const isTriggerSegment = LOOP_TRIGGERS.has(segment.segment_id);

            if (isTriggerSegment) {
                // Trigger segments always start a new loop instance
                if (currentLoop && currentLoopSegments.length > 0) {
                    // Save previous loop
                    hierarchy.push({
                        type: 'loop',
                        loopId: currentLoop.loop,
                        loopName: currentLoop.name,
                        segments: currentLoopSegments,
                        startIndex: currentLoopSegments[0].index
                    });
                }
                // Start new loop with this trigger segment
                currentLoop = loopContext;
                currentLoopSegments = [{ ...segment, index }];
            } else {
                // Child segment (like N2, N3, N4, PER, PID, SCH, etc.)
                if (currentLoop && currentLoop.loop === loopContext.loop) {
                    // Continue current loop if same type
                    currentLoopSegments.push({ ...segment, index });
                } else {
                    // Start new loop if no current loop or different loop type
                    if (currentLoop && currentLoopSegments.length > 0) {
                        hierarchy.push({
                            type: 'loop',
                            loopId: currentLoop.loop,
                            loopName: currentLoop.name,
                            segments: currentLoopSegments,
                            startIndex: currentLoopSegments[0].index
                        });
                    }
                    currentLoop = loopContext;
                    currentLoopSegments = [{ ...segment, index }];
                }
            }
        } else {
            // This segment is not part of a loop
            if (currentLoop && currentLoopSegments.length > 0) {
                // Save current loop before adding standalone segment
                hierarchy.push({
                    type: 'loop',
                    loopId: currentLoop.loop,
                    loopName: currentLoop.name,
                    segments: currentLoopSegments,
                    startIndex: currentLoopSegments[0].index
                });
                currentLoop = null;
                currentLoopSegments = [];
            }
            // Add standalone segment
            hierarchy.push({
                type: 'segment',
                segment: { ...segment, index }
            });
        }
    });

    // Don't forget the last loop
    if (currentLoop && currentLoopSegments.length > 0) {
        hierarchy.push({
            type: 'loop',
            loopId: currentLoop.loop,
            loopName: currentLoop.name,
            segments: currentLoopSegments,
            startIndex: currentLoopSegments[0].index
        });
    }

    return hierarchy;
}

export default EDIPrettyViewer;
